/**
 * The day/night terminator, where it actually is right now.
 *
 * This is the one piece of globe decoration that is also content: for a
 * geography game, showing real night is a fact about the world, not a mood.
 * Someone will check it against sunrise where they live, so the astronomy is
 * the real thing (NOAA's solar position algorithm) rather than a tilted circle.
 *
 * WHAT THIS RENDERS. Night is a spherical cap centred on the antisolar point.
 * A hard cap at exactly 90 degrees would read as a bug — real twilight grades
 * over about 18 degrees — so it ships as nested caps at the standard twilight
 * boundaries, each a little darker than the last. The result is a soft band
 * roughly 1200 km wide, which is what it is on Earth.
 *
 * INVARIANT 7 is untouched: these are fill polygons, no text of any kind.
 */

import type { Feature, FeatureCollection, Polygon } from 'geojson';

/** Days from the J2000.0 epoch to `date`. */
function julianCenturies(date: Date): number {
  const jd = date.getTime() / 86_400_000 + 2_440_587.5;
  return (jd - 2_451_545) / 36_525;
}

/**
 * Where the sun is directly overhead, as `[lng, lat]`.
 *
 * NOAA's low-precision algorithm — good to well under a degree, which is far
 * finer than a terminator drawn at globe zoom can show.
 */
export function subsolarPoint(date: Date): [number, number] {
  const t = julianCenturies(date);
  const rad = Math.PI / 180;

  // Geometric mean longitude and anomaly of the sun.
  const l0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
  const m = 357.52911 + t * (35999.05029 - t * 0.0001537);

  // Equation of centre -> true longitude -> apparent longitude.
  const c =
    Math.sin(m * rad) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(2 * m * rad) * (0.019993 - 0.000101 * t) +
    Math.sin(3 * m * rad) * 0.000289;
  const trueLong = l0 + c;
  const omega = 125.04 - 1934.136 * t;
  const appLong = trueLong - 0.00569 - 0.00478 * Math.sin(omega * rad);

  // Obliquity of the ecliptic, corrected for nutation.
  const e0 =
    23 +
    (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
  const e = e0 + 0.00256 * Math.cos(omega * rad);

  const declination =
    Math.asin(Math.sin(e * rad) * Math.sin(appLong * rad)) / rad;

  // Equation of time, in minutes.
  const y = Math.tan((e / 2) * rad) ** 2;
  const eqTime =
    4 *
    (y * Math.sin(2 * l0 * rad) -
      2 * 0.016708634 * Math.sin(m * rad) +
      4 * 0.016708634 * y * Math.sin(m * rad) * Math.cos(2 * l0 * rad) -
      0.5 * y * y * Math.sin(4 * l0 * rad) -
      1.25 * 0.016708634 ** 2 * Math.sin(2 * m * rad)) /
    rad;

  const utcMinutes =
    date.getUTCHours() * 60 + date.getUTCMinutes() + date.getUTCSeconds() / 60;
  // Solar noon sits on the meridian where local apparent time is 12:00.
  let longitude = -((utcMinutes + eqTime) / 4 - 180);
  longitude = (((longitude + 180) % 360) + 360) % 360 - 180;

  return [longitude, declination];
}

/** The point directly opposite `[lng, lat]` — the centre of night. */
export function antipode([lng, lat]: [number, number]): [number, number] {
  const opp = lng > 0 ? lng - 180 : lng + 180;
  return [opp, -lat];
}

/**
 * A spherical cap of angular radius `radiusDeg` about `centre`, as a polygon.
 *
 * Densified hard, because under globe projection a polygon edge is drawn as a
 * straight chord between its vertices — too few and the terminator becomes a
 * visible polygon rather than a circle.
 */
export function sphericalCap(
  centre: [number, number],
  radiusDeg: number,
  steps = 180,
): Polygon {
  const rad = Math.PI / 180;
  const [clng, clat] = centre;
  const lat1 = clat * rad;
  const lng1 = clng * rad;
  const d = radiusDeg * rad;

  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i += 1) {
    // Clockwise so the ring winds the way GeoJSON wants for an outer ring.
    const bearing = ((steps - i) / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(bearing),
    );
    const lng2 =
      lng1 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(d) * Math.cos(lat1),
        Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
      );
    const lng = (((lng2 / rad + 180) % 360) + 360) % 360 - 180;
    ring.push([lng, lat2 / rad]);
  }
  return { type: 'Polygon', coordinates: [ring] };
}

/**
 * Twilight boundaries, in degrees of solar depression below the horizon.
 *
 * A cap of radius `90 + depression` about the antisolar point is exactly the
 * region where the sun sits at least `depression` below the horizon.
 */
export const TWILIGHT_BANDS = [
  { depression: 0, opacity: 0.16 }, // sunset/sunrise line outward
  { depression: 6, opacity: 0.3 }, // civil
  { depression: 12, opacity: 0.42 }, // nautical
  { depression: 18, opacity: 0.55 }, // astronomical — full night
] as const;

export interface NightBandProps {
  /** Stacking index; also drives opacity via a `match` in the style. */
  readonly band: number;
}

/**
 * Night as a stack of nested caps, darkest last.
 *
 * They overlap deliberately — each is drawn over the previous, so the alphas
 * compound into a smooth ramp instead of four visible steps.
 */
export function nightBands(date: Date): FeatureCollection<Polygon, NightBandProps> {
  const centre = antipode(subsolarPoint(date));
  return {
    type: 'FeatureCollection',
    features: TWILIGHT_BANDS.map(
      (b, i): Feature<Polygon, NightBandProps> => ({
        type: 'Feature',
        properties: { band: i },
        geometry: sphericalCap(centre, 90 + b.depression),
      }),
    ),
  };
}

export function emptyNightBands(): FeatureCollection<Polygon, NightBandProps> {
  return { type: 'FeatureCollection', features: [] };
}
